import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, MapPin, Wrench, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const iconMap = { Phone, MapPin, Wrench, CheckCircle };

const HowItWorksSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  const steps = t('howItWorks.steps') || [];

  return (
    <section id="how-it-works" ref={ref} className="py-16 sm:py-28 relative noise-overlay" style={{ background: 'linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)' }}>
      <div className="gold-line mb-10 sm:mb-16" />

      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14 sm:mb-24"
        >
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black mb-4 sm:mb-5">
            <span className="gold-text-shimmer">{t('howItWorks.headerTitle')}</span>
          </h2>
          <p className="text-blue-100 text-sm sm:text-lg max-w-2xl mx-auto px-2">
            {t('howItWorks.headerSubtitle')}
          </p>
        </motion.div>

        {/* Mobile: Simple vertical list, Desktop: Zigzag */}
        <div className="relative">
          {/* Desktop vertical line */}
          <div
            className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2"
            style={{ background: 'linear-gradient(180deg, rgba(96, 165, 250, 0.2), rgba(96, 165, 250, 0.05))' }}
          />

          {/* Mobile vertical line */}
          <div
            className="lg:hidden absolute right-[23px] top-0 bottom-0 w-px"
            style={{ background: 'linear-gradient(180deg, rgba(96, 165, 250, 0.15), rgba(96, 165, 250, 0.03))' }}
          />

          <div className="space-y-6 lg:space-y-0">
            {steps.map((step, index) => {
              const IconComponent = iconMap[['Phone', 'MapPin', 'Wrench', 'CheckCircle'][index]];
              const isEven = index % 2 === 0;
              const stepNumber = String(index + 1).padStart(2, '0');

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 25 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.15 * index, duration: 0.6 }}
                >
                  {/* Mobile layout */}
                  <div className="lg:hidden flex items-start gap-4">
                    {/* Number circle */}
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0 relative z-10"
                      style={{
                        background: 'linear-gradient(135deg, #c9a84c, #e8d48b)',
                        color: '#0b1a32',
                        boxShadow: '0 0 15px rgba(96, 165, 250, 0.15)',
                      }}
                    >
                      {stepNumber}
                    </div>
                    {/* Content */}
                    <div
                      className="flex-1 p-5 mb-2"
                      style={{
                        borderRadius: '1.2rem',
                        background: 'rgba(14, 30, 56, 0.4)',
                      }}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        {IconComponent && <IconComponent size={18} style={{ color: '#ffffff' }} />}
                        <h3 className="text-base font-bold text-white">{step.title}</h3>
                      </div>
                      <p className="text-blue-100 text-xs leading-relaxed">{step.description}</p>
                    </div>
                  </div>

                  {/* Desktop zigzag layout */}
                  <div className={`hidden lg:flex items-center py-10 ${isEven ? 'flex-row' : 'flex-row-reverse'}`}>
                    <div className={`w-1/2 ${isEven ? 'text-left pl-16' : 'text-right pr-16'}`}>
                      <div
                        className="p-8 transition-all duration-400 group cursor-default"
                        style={{
                          borderRadius: '1.5rem',
                          background: 'rgba(14, 30, 56, 0.4)',
                          borderBottom: '2px solid transparent',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderBottomColor = 'rgba(96, 165, 250, 0.25)';
                          e.currentTarget.style.background = 'rgba(30, 58, 138, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderBottomColor = 'transparent';
                          e.currentTarget.style.background = 'rgba(14, 30, 56, 0.4)';
                        }}
                      >
                        <div className="flex items-center gap-4 mb-4">
                          {IconComponent && <IconComponent size={24} style={{ color: '#6ee7b7' }} />}
                          <h3 className="text-xl font-bold text-white">{step.title}</h3>
                        </div>
                        <p className="text-blue-100 text-sm leading-relaxed">{step.description}</p>
                      </div>
                    </div>

                    {/* Center number */}
                    <div className="w-20 flex items-center justify-center relative z-10">
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center text-lg font-black"
                        style={{
                          background: 'linear-gradient(135deg, #c9a84c, #e8d48b)',
                          color: '#0b1a32',
                          boxShadow: '0 4px 20px rgba(96, 165, 250, 0.2)',
                        }}
                      >
                        {stepNumber}
                      </div>
                    </div>

                    {/* Empty space on other side */}
                    <div className="w-1/2" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-24" style={{ background: 'linear-gradient(to top, #1e40af, transparent)' }} />
    </section>
  );
};

export default HowItWorksSection;
